import React from 'react';
import { Button } from '@/components/ui/button';
import { Lock } from 'phosphor-react';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import { ANALYTICS_CURRENCIES, ANALYTICS_RANGES, ANALYTICS_UNDERLYINGS, formatStrategyLabel } from './optionsAnalyticsUtils';
import { getRangeLabel } from './optionsDashboardUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { OptionsCurrencyDisplay } from './OptionsCurrencyDisplay';
import { OptionsAssetIcon } from './OptionsAssetIcon';
import { isOptionsCustomPeriodRestricted } from './optionsFreemiumUtils';

export const OptionsAnalyticsControls = ({
  selectedCurrency,
  onCurrencyChange,
  selectedRange,
  onRangeChange,
  customRange,
  onCustomRangeChange,
  onApplyCustomRange,
  underlyingFilter,
  onUnderlyingChange,
  strategyFilter,
  onStrategyChange,
  strategyOptions = ['all'],
}) => {
  const { t } = useOptionsLocale();
  const { isPro, openUpgradeModal } = useOptionsEntitlements();

  return (
    <div className="space-y-3" data-testid="options-analytics-controls">
      <FilterGroup label={t('common.view')} testId="options-analytics-currency-filter">
        <div className="inline-flex flex-wrap items-center gap-1.5 rounded-[18px] border border-white/10 bg-[#11161E] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]" data-testid="options-analytics-currency-tabs">
          {ANALYTICS_CURRENCIES.map((currency) => {
            const isActive = selectedCurrency === currency;
            return (
              <button key={currency} type="button" onClick={() => onCurrencyChange(currency)} className={`inline-flex items-center rounded-[14px] px-3 py-2 text-sm font-semibold transition-all duration-200 font-body ${isActive ? 'bg-[#202A38] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]' : 'text-white/62 hover:bg-white/[0.04] hover:text-white'}`} data-typography="nav" data-testid={`options-analytics-currency-${currency}`}>
                <OptionsCurrencyDisplay currencyKey={currency} compact iconSize={16} />
              </button>
            );
          })}
        </div>
      </FilterGroup>

      <div className="grid gap-3 xl:grid-cols-[1.1fr,0.9fr,1.4fr]" data-testid="options-analytics-filter-grid">
        <FilterGroup label={t('common.period')} testId="options-analytics-range-filter">
          <div className="inline-flex flex-wrap items-center gap-1 rounded-2xl border border-white/10 bg-white/[0.03] p-1" data-testid="options-analytics-range-tabs">
            {ANALYTICS_RANGES.map((range) => {
              const isActive = selectedRange === range;
              const isRestricted = isOptionsCustomPeriodRestricted({ isPro, range });
              
              const handleClick = () => {
                if (isRestricted) {
                  openUpgradeModal("ANALYTICS");
                  return;
                }
                onRangeChange(range);
              };

              return (
                <button 
                  key={range} 
                  type="button" 
                  onClick={handleClick} 
                  className={`inline-flex items-center gap-1 rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-200 font-body ${isActive ? 'bg-white text-black' : 'text-white/60 hover:bg-white/5 hover:text-white'}`} 
                  data-typography="nav" 
                  data-testid={`options-analytics-range-${range}`}
                >
                  {isRestricted && <Lock size={12} className="text-[#A855F7]" weight="fill" />}
                  {getRangeLabel(range, t)}
                  {isRestricted && <span className="text-[9px] bg-[#A855F7]/10 text-[#A855F7] px-1.5 py-0.5 rounded-sm font-bold">PRO</span>}
                </button>
              );
            })}
          </div>

          {selectedRange === 'custom' ? (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="options-analytics-custom-range-controls">
              <input type="date" value={customRange.startDate} onChange={(event) => onCustomRangeChange((current) => ({ ...current, startDate: event.target.value }))} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none" data-testid="options-analytics-custom-range-start" />
              <input type="date" value={customRange.endDate} onChange={(event) => onCustomRangeChange((current) => ({ ...current, endDate: event.target.value }))} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none" data-testid="options-analytics-custom-range-end" />
              <Button variant="outline" onClick={onApplyCustomRange} data-testid="options-analytics-custom-range-apply">{t('common.apply')}</Button>
            </div>
          ) : null}
        </FilterGroup>

        <FilterGroup label={t('common.underlying')} testId="options-analytics-underlying-filter">
          <div className="inline-flex flex-wrap rounded-2xl border border-white/10 bg-white/[0.03] p-1" data-testid="options-analytics-underlying-tabs">
            {ANALYTICS_UNDERLYINGS.map((underlying) => {
              const isActive = underlyingFilter === underlying;
              return (
                <button key={underlying} type="button" onClick={() => onUnderlyingChange(underlying)} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors duration-200 font-body ${isActive ? 'bg-white text-black' : 'text-white/60 hover:bg-white/5 hover:text-white'}`} data-typography="nav" data-testid={`options-analytics-underlying-${underlying}`}>
                  {underlying === 'all' ? t('common.all') : <><OptionsAssetIcon assetKey={underlying} size={14} /> {underlying.toUpperCase()}</>}
                </button>
              );
            })}
          </div>
        </FilterGroup>

        <FilterGroup label={t('common.strategy')} testId="options-analytics-strategy-filter">
          <div className="inline-flex flex-wrap rounded-2xl border border-white/10 bg-white/[0.03] p-1" data-testid="options-analytics-strategy-tabs">
            {strategyOptions.map((strategy) => {
              const isActive = strategyFilter === strategy;
              return (
                <button key={strategy} type="button" onClick={() => onStrategyChange(strategy)} className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors duration-200 font-body ${isActive ? 'bg-white text-black' : 'text-white/60 hover:bg-white/5 hover:text-white'}`} data-typography="nav" data-testid={`options-analytics-strategy-${strategy.toLowerCase()}`}>
                  {strategy === 'all' ? t('common.all') : formatStrategyLabel(strategy)}
                </button>
              );
            })}
          </div>
        </FilterGroup>
      </div>
    </div>
  );
};

const FilterGroup = ({ label, testId, children }) => (
  <div className="space-y-2" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40" data-typography="label" data-testid={`${testId}-label`}>{label}</p>
    {children}
  </div>
);

export default OptionsAnalyticsControls;